import React from "react";
import { AbsoluteFill, Sequence, getRemotionEnvironment, useCurrentFrame, useVideoConfig } from "remotion";
import type { AudioTrack, BodyScene, Frame } from "@veyso/core";
import { BackgroundLayer } from "../components/Background.js";
import { useVideoComponent } from "../components/VideoLayer.js";
import {
  FocusOverlay,
  activeFocus,
  activeFocusEffects,
  blurRadius,
  blurRegionStyle,
  focusTransform,
} from "../components/Focus.js";
import { useAssetUrl } from "../assets.js";

/**
 * The recorded BODY. The source video is only ever transformed — zoom and pan
 * are CSS transforms on a wrapper, never a crop — so the original recording
 * remains the visual truth and every effect is reversible in the editor.
 */
export const BodySection: React.FC<{
  scene: BodyScene;
  legacyOriginalAudio?: AudioTrack;
  /** The editor's chosen preview proxy; never consulted while rendering. */
  previewProxyKey?: string;
}> = ({ scene, legacyOriginalAudio, previewProxyKey }) => {
  const assetUrl = useAssetUrl();
  const frame = useCurrentFrame();
  const { fps, width, height } = useVideoConfig();
  // OffthreadVideo on the server, <Video> in a browser render. Unset means the
  // former, so nothing changes for the worker or the editor preview.

  // Overlays composite; only one effect may move the camera.
  const overlays = activeFocusEffects(scene.focus, fps, frame);
  const transform = focusTransform(activeFocus(scene.focus, fps, frame));

  // The recording's audio is extracted to its own asset and played as an
  // ordinary track, so the video element itself is silent. The exception is a
  // timeline saved before that change, whose original track has no file of its
  // own — muting that unconditionally would make an existing project go quiet.
  const volume = legacyOriginalAudio?.volume ?? 0;

  // The preview plays the proxy, the render plays the original. A browser
  // cannot seek a variable-frame-rate screen recording without jumping
  // backwards, and the proxy exists to give it a fixed cadence; the encoder has
  // no such trouble and should always see full quality.
  const source = getRemotionEnvironment().isPlayer
    ? (previewProxyKey ?? scene.proxyKey ?? scene.assetKey)
    : scene.assetKey;

  // Inset, rounded and shadowed — or, when the frame is off, exactly the
  // full-bleed layout this always had. The zoom transform lives *inside* the
  // card so a zoom magnifies the recording rather than the card itself, and the
  // card's own corners stay put while it happens.
  // Named `card`, not `frame` — `frame` is already the current frame number.
  const card = scene.frame;
  const inset = card?.enabled ? Math.round(width * card.padding) : 0;
  const radius = card?.enabled ? card.radius : 0;

  const { width: cardWidth, height: cardHeight } = cardBox(
    width,
    height,
    inset,
    card?.enabled ? scene.aspect : undefined,
  );

  const clips = videoClips(scene.segments, scene.trimStart, scene.duration, fps);

  return (
    <AbsoluteFill>
      <BackgroundLayer background={scene.background} />
      <AbsoluteFill style={{ padding: inset, alignItems: "center", justifyContent: "center", flexDirection: "row" }}>
        <div
          style={{
            position: "relative",
            // Takes the recording's own shape, so the rounded corners sit on
            // the picture rather than on letterbox bars. Without an aspect —
            // timelines built before it was recorded — this is the full padded
            // area, exactly as before.
            width: cardWidth,
            height: cardHeight,
            borderRadius: radius,
            overflow: "hidden",
            // Opaque, so the card is never a window onto the background.
            // A frame the video has not decoded yet paints nothing, and without
            // this the backdrop showed through the hole where the footage
            // belongs — which reads as the recording having merged into the
            // background rather than as a frame still loading.
            backgroundColor: "#000",
            boxShadow: shadowFor(card?.enabled ? card.shadow : "none", width),
          }}
        >
          <AbsoluteFill style={transform}>
            {/* One clip per kept stretch, laid end to end.
                This is what a cut *is* now — not a re-encoded copy of the
                recording, just the same file entered at different points. With
                no segments it is a single clip from `trimStart`, which is every
                project that has never been cut. */}
            <Clips clips={clips} src={assetUrl(source)!} muted={!legacyOriginalAudio} volume={volume} fit={scene.fit} />

            {/* Blurred regions, drawn as a second clipped copy of the footage.
                See `BlurredRegion` for why this is a copy rather than the one
                div a `backdrop-filter` would have been. Muted and silent:
                a second copy of the audio is an echo. */}
            {overlays
              .filter((entry) => entry.effect.mode === "blur" && entry.effect.target)
              .map((entry) => (
                <BlurredRegion
                  key={entry.effect.id}
                  target={entry.effect.target!}
                  radius={blurRadius(entry.effect.blur, entry.amount)}
                >
                  <Clips clips={clips} src={assetUrl(source)!} muted volume={0} fit={scene.fit} />
                </BlurredRegion>
              ))}

            {/* The card's own size, which the dim needs in pixels: the
                exporter's clip-path parser hands the path straight to
                `Path2D`, so percentages are not an option. This is the box the
                overlay fills, and this is the only place that knows it. */}
            <FocusOverlay active={overlays} fps={fps} box={{ width: cardWidth, height: cardHeight }} />
          </AbsoluteFill>
        </div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

/**
 * The recording, as a list of clips to play in order.
 *
 * Its own component because the blurred regions draw it a second time. That
 * duplication is the price of a blur the browser can render: `backdrop-filter`
 * blurs what is behind an element in one div, and the browser's exporter
 * cannot draw it at all — so a blurred project used to be a project that could
 * only export on a worker, for a fee.
 */
const Clips: React.FC<{
  clips: ReturnType<typeof videoClips>;
  src: string;
  muted: boolean;
  volume: number | ((frame: number) => number);
  fit: BodyScene["fit"];
}> = ({ clips, src, muted, volume, fit }) => {
  // Resolved here rather than passed in: which media component to use is a
  // property of the renderer, not of the caller, and threading it through
  // would be a second place for the two copies to disagree.
  const VideoLayer = useVideoComponent();

  return (
    <>
      {clips.map((clip) => (
        <Sequence
          key={`${clip.from}-${clip.trimBefore}`}
          from={clip.from}
          durationInFrames={clip.durationInFrames}
          layout="none"
          name={`clip-${clip.from}`}
        >
          <VideoLayer
            src={src}
            muted={muted}
            volume={volume}
            trimBefore={clip.trimBefore > 0 ? clip.trimBefore : undefined}
            style={{ width: "100%", height: "100%", objectFit: fit }}
          />
        </Sequence>
      ))}
    </>
  );
};

/**
 * One blurred region: the whole frame again, blurred, clipped to a rectangle.
 *
 * `backdrop-filter` was the obvious way to do this and was the way it was done
 * — one div over the picture, blurring whatever is behind it, no second decode
 * and nothing that can fall out of step. The browser's exporter cannot draw it.
 * It re-implements CSS painting with Canvas 2D calls and has no equivalent, so
 * the blur simply did not appear: a region somebody blurred to hide a customer
 * name, exported in a tab, came out legible.
 *
 * `filter` it can draw — that is `ctx.filter`, which Canvas has natively. So
 * the frame is drawn a second time with `filter: blur()` on it and clipped to
 * the rectangle, which both renderers agree on.
 *
 * The inner offsets are what make the copy line up. The outer box is the
 * region; the inner is the *whole frame* at the size it would be, shifted so
 * that the part showing through the hole is the part that was there anyway.
 *
 * It costs a second decode of the source for each blurred region, which is the
 * trade: a blur that renders for free in somebody's own browser, against a
 * blur that was free to draw and cost a server render to export.
 */
const BlurredRegion: React.FC<{
  target: { x: number; y: number; width: number; height: number };
  radius: number;
  children: React.ReactNode;
}> = ({ target, radius, children }) => {
  const { clip, copy } = blurRegionStyle(target);

  return (
    <div style={clip}>
      <div style={{ ...copy, filter: `blur(${radius}px)` }}>{children}</div>
    </div>
  );
};

/**
 * Which stretches of the source play, and from where.
 *
 * Each entry says where it sits on the finished timeline (`from`), how long it
 * runs, and how far into the source file to start. Worked out in whole frames
 * because a clip that begins half a frame late shows the tail of the one
 * before it, and at a cut point that reads as a glitch rather than an edit.
 *
 * With no segments this is a single clip covering the whole body — the shape
 * every uncut project has always had, so nothing changes for them.
 */
export function videoClips(
  segments: { start: number; end: number }[] | undefined,
  trimStart: number,
  duration: number,
  fps: number,
): { from: number; durationInFrames: number; trimBefore: number }[] {
  const usable = (segments ?? []).filter((segment) => segment.end > segment.start);
  if (usable.length === 0) {
    return [
      {
        from: 0,
        durationInFrames: Math.max(1, Math.round(duration * fps)),
        trimBefore: Math.round(trimStart * fps),
      },
    ];
  }

  const clips: { from: number; durationInFrames: number; trimBefore: number }[] = [];
  let from = 0;
  for (const segment of usable) {
    const durationInFrames = Math.max(1, Math.round((segment.end - segment.start) * fps));
    clips.push({ from, durationInFrames, trimBefore: Math.round(segment.start * fps) });
    from += durationInFrames;
  }
  return clips;
}

/**
 * The card's box, worked out rather than left to CSS.
 *
 * Two things this has to get right, both learned the hard way.
 *
 * `aspect-ratio` cannot satisfy a fixed width and a max height at once — give
 * it both and it keeps the width and hands back a differently shaped box. The
 * composition is a known, fixed pixel space, so the arithmetic is exact and
 * belongs here.
 *
 * And the box must equal the *picture*, not the space available to it. A card
 * shaped like the padded area holds a `contain`-fitted recording of a different
 * shape with bars down its sides, and the corners and shadow then trace an edge
 * the viewer cannot see — which reads as a second card sitting behind the
 * first. With no aspect to work from there is nothing better to do than fill
 * the area, which is what this did before the frame existed.
 */
export function cardBox(
  width: number,
  height: number,
  inset: number,
  aspect: number | undefined,
): { width: number; height: number } {
  const availableWidth = width - inset * 2;
  const availableHeight = height - inset * 2;
  if (!aspect || aspect <= 0) return { width: availableWidth, height: availableHeight };

  const cardWidth = Math.min(availableWidth, availableHeight * aspect);
  return { width: cardWidth, height: cardWidth / aspect };
}

/**
 * The three shadow depths.
 *
 * Scaled by the composition width so the same preset reads identically at 720p
 * and 4K — a shadow measured in fixed pixels vanishes as the output grows.
 * Two layers each: a tight contact shadow that anchors the card to the
 * background, and a wide soft one that gives it height. One layer alone looks
 * either pasted on or smudged.
 */
function shadowFor(shadow: Frame["shadow"] | undefined, width: number): string | undefined {
  if (!shadow || shadow === "none") return undefined;
  const u = width / 1920;
  return shadow === "strong"
    ? `0 ${8 * u}px ${24 * u}px rgba(0,0,0,0.28), 0 ${48 * u}px ${96 * u}px rgba(0,0,0,0.34)`
    : `0 ${4 * u}px ${12 * u}px rgba(0,0,0,0.16), 0 ${24 * u}px ${56 * u}px rgba(0,0,0,0.20)`;
}
